/**
 * Shipping Protection Custom Element
 * Handles automatic adding/removing of shipping protection variant and UI synchronization.
 */

if (!customElements.get('shipping-protection')) {
  customElements.define(
    'shipping-protection',
    class ShippingProtection extends HTMLElement {
      constructor() {
        super();
        this.variantId = this.getAttribute('data-variant-id');
        this.checkbox = this.querySelector('input[type="checkbox"]');
        this.isShippingProtectionRunning = false;
      }

      connectedCallback() {
        if (!this.checkbox || !this.variantId) return;

        // Sync initial state
        this.init();

        // Listen for checkbox changes
        this.checkbox.addEventListener('change', this.onCheckboxChange.bind(this));

        // Listen for cart updates from other sources
        this.cartUpdateUnsubscriber = theme.pubsub.subscribe(
          theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
          this.onCartUpdate.bind(this)
        );
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      async init() {
        if (this.isShippingProtectionRunning) return;
        this.isShippingProtectionRunning = true;

        try {
          const cart = await this.getCart();
          const variantIdNum = Number(this.variantId);
          const existingItem = cart.items.find((item) => item.variant_id === variantIdNum);
          const hasOtherItems = cart.items.some((item) => item.variant_id !== variantIdNum);
          const isOptedOut = localStorage.getItem('shipping_protection_opt_out') === 'true';

          // Case 0: Cart is empty or only has shipping protection -> Remove it
          if (cart.item_count === 0 || !hasOtherItems) {
            if (existingItem) {
              await this.updateCartQuantity(existingItem.key, 0);
            }
            this.checkbox.checked = false;
            // Don't remove opt-out here, maybe user wants it off next time they add something
            this.isShippingProtectionRunning = false;
            return;
          }

          // Case 1: Auto Add removed (now handled by ProductForm to prevent double updates)

          // Case 2: Auto Remove (if in cart but opt-out is true)
          if (existingItem && isOptedOut) {
            this.checkbox.checked = false;
            await this.updateCartQuantity(existingItem.key, 0);
            return;
          }

          // Case 3: Ensure quantity is always 1 (if in cart)
          if (existingItem && existingItem.quantity !== 1) {
            await this.updateCartQuantity(existingItem.key, 1);
            return;
          }

          // Case 4: Sync Checkbox UI
          this.checkbox.checked = !!existingItem && !isOptedOut;
        } catch (error) {
          console.error('Shipping Protection Init Error:', error);
        } finally {
          this.isShippingProtectionRunning = false;
        }
      }

      async onCheckboxChange() {
        if (this.isShippingProtectionRunning) return;
        this.isShippingProtectionRunning = true;

        try {
          // Add a 1.5 second delay before processing the toggle
          await new Promise((resolve) => setTimeout(resolve, 1000));

          const variantIdNum = Number(this.variantId);
          if (this.checkbox.checked) {
            localStorage.removeItem('shipping_protection_opt_out');
            await this.addToCart(variantIdNum, 1);
          } else {
            localStorage.setItem('shipping_protection_opt_out', 'true');
            const cart = await this.getCart();
            const item = cart.items.find((it) => it.variant_id === variantIdNum);
            if (item) {
              await this.updateCartQuantity(item.key, 0);
            }
          }
        } catch (error) {
          console.error('Shipping Protection Toggle Error:', error);
        } finally {
          this.isShippingProtectionRunning = false;
        }
      }

      onCartUpdate(event) {
        // Prevent infinite loops if the update came from here
        if (event.source === 'shipping-protection') return;

        // Re-run init logic to ensure SP is still correctly in/out of cart
        // We delay this by 1.5 seconds to allow other cart updates to settle
        clearTimeout(this.updateTimeout);
        this.updateTimeout = setTimeout(() => {
          this.init();
        }, 1500);
      }

      async getCart() {
        const res = await fetch(theme.routes.cart_url + '.js');
        return await res.json();
      }

      async addToCart(id, quantity) {
        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:bundled-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle }
          })
        );

        const res = await fetch(theme.routes.cart_add_url + '.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: [{ id, quantity }],
            sections: sectionsToBundle
          })
        });
        const parsedState = await res.json();

        // Shopify's /cart/add.js doesn't return the full cart, so we fetch it
        const cartRes = await fetch(theme.routes.cart_url + '.js');
        const cartJson = await cartRes.json();
        cartJson.sections = parsedState.sections;

        // Publish update to theme
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'shipping-protection',
          cart: cartJson
        });
      }

      async updateCartQuantity(key, quantity) {
        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:bundled-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle }
          })
        );

        const res = await fetch(theme.routes.cart_change_url + '.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: key,
            quantity,
            sections: sectionsToBundle
          })
        });
        const parsedState = await res.json();

        // Publish update to theme
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'shipping-protection',
          cart: parsedState
        });
      }
    }
  );
}
